import type {Command, GlobalAttributes} from '@tiptap/core';
import {Extension} from '@tiptap/core';
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {NodeType} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/TipTapEditor";
import {dekodTallMedBenevnelse, erIkkeTomCellSelection} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/tiptapHjelpere";
import {nil} from "felles-ng2/typer/nullish";
import {finnElementetsNærmesteForelderAvType} from "felles-ng2/støttefunksjoner/dom";

/** Tiptap utvidelse som legger til attributtet 'ramme' for bruk på tabell-celler (tableCell og tableHead) */
export const TabellcelleRamme = Extension.create({

    name: 'TabellcelleRamme',

    /** Legger til ramme-attributtet på både 'tableCell' og 'tableHeader', bruker derfor addGlobalAttributes */
    addGlobalAttributes(): GlobalAttributes {
        return [
            {
                types: tillatteNodetyper(),
                attributes: {
                    ramme: {
                        default: {venstre: true, topp: true, høyre: true, bunn: true, farge: '#000000', tykkelse: 1} as Celleramme,
                        renderHTML: attributter => tabellcelleRammeSomHtmlStyleAttributt(attributter),
                        parseHTML: element => htmlBorderStyleSomRammeAttributt(element),
                        keepOnSplit: true,
                    }
                }
            }
        ];
    },

    addCommands(): TabellcelleRammeKommandoer<Command> {
        return {
            setCelleRamme: (ramme) => props => {
                if (erNil(props.dispatch)) return erIkkeTomCellSelection(props.tr.selection); //kommandoen kalles i dry-run-modus
                return tillatteNodetyper().reduce((chain, type) =>
                  chain.updateAttributes(type, {ramme: ramme}), props.chain())
                  .run();
            },

            fjernCelleRamme: () => props => {
                if (erNil(props.dispatch)) return erIkkeTomCellSelection(props.tr.selection); //kommandoen kalles i dry-run-modus
                return tillatteNodetyper().reduce((chain, type) =>
                  chain.updateAttributes(type, {ramme: null}), props.chain())
                  .run();
            }
        };
    }
});

function tillatteNodetyper() {
    return [NodeType.TableCell, NodeType.TableHeader];
}

/** Style-attributter som administreres av {@link TabellcelleRamme}.
 * Merk at ramme kan angis via css style attributtet 'border', men skriver alltid ramme som 'border-left', 'border-top', 'border-right', 'border-bottom' uten 'border' */
export const styleAttributterForRamme = ['border', 'border-left', 'border-top', 'border-right', 'border-bottom', 'border-color', 'border-bottom']

export function erLikeRammevalg(a: Celleramme | nil, b: Celleramme | nil): boolean {
    return erIkkeNil(a) && erIkkeNil(b) &&
      a?.bunn == b?.bunn &&
      a?.høyre == b?.høyre &&
      a?.topp == b?.topp &&
      a?.venstre == b?.venstre &&
      a?.tykkelse == b?.tykkelse &&
      a?.farge == b?.farge;
}

/** Serialiserer 'ramme' attributtet ({@link Celleramme}) til html css style attributter på formen {style: <css-ramme-properties som streng>} */
export function tabellcelleRammeSomHtmlStyleAttributt(attributter: Record<string, any>): Record<string, any> {
    const cssFragment = somCssStyle(attributter.ramme);
    //console.log('#TabellcelleRamme.rammeSomHtmlStyleAttributt', ingenRammerSatt(attributter.ramme), attributter.ramme, cssFragment);
    return erIkkeNil(cssFragment) ? {style: cssFragment!} : {};
}

function somCssStyle(rammevalg: Celleramme | nil) {
    if (erNil(rammevalg) || ingenRammerSatt(rammevalg)) return null;
    const borderStil = `${rammevalg.tykkelse}px solid ${rammevalg.farge}`;
    const cssProperties = new Map<string, any>();
    if (rammevalg.venstre) cssProperties.set('border-left', borderStil);
    if (rammevalg.topp) cssProperties.set('border-top', borderStil);
    if (rammevalg.høyre) cssProperties.set('border-right', borderStil);
    if (rammevalg.bunn) cssProperties.set('border-bottom', borderStil);
    return [...cssProperties.entries()].map(([property, verdi]) => `${property}: ${verdi}`).join('; ');
}

function ingenRammerSatt(rammer: Celleramme) {
    return erIkkeNil(rammer) &&
      !rammer.venstre && !rammer.topp && !rammer.høyre && !rammer.bunn;
}

/** Deserialiserer css border style properties satt på angitt element til {@link Celleramme} representasjon */
function htmlBorderStyleSomRammeAttributt(element: HTMLElement): Celleramme {
    const rammer = {
        venstre: harRamme(element, 'Left'),
        topp: harRamme(element, 'Top'),
        høyre: harRamme(element, 'Right'),
        bunn: harRamme(element, 'Bottom'),
        farge: element.style.borderColor,
        tykkelse: dekodTallMedBenevnelse(element.style.borderWidth, 'px', 1)
    };
    //dersom ingen rammer er satt via style-attributter ser vi om border-attributtet er satt på table-elementet
    //vi gjør dette da ckeditor som standard lager tabeller med border=1 satt på tabell, og uten å fange opp dette vil slike tabeller bli tegnet ut uten ramme
    //siden vi ikke har konfigurert tiptap/utvidelser/Tabell.ts til å akseptere border attributtet (dermed blir det fjernet av tiptap)
    if (!rammer.venstre && !rammer.topp && !rammer.høyre && !rammer.bunn) {
        const tabellBorder = parseInt(finnElementetsNærmesteForelderAvType(element, 'table')?.getAttribute('border') ?? '');
        if (tabellBorder > 0)
            return {venstre: true, topp: true, høyre: true, bunn: true, farge: '#000000', tykkelse: tabellBorder};
    }
    return rammer;
}

function harRamme(element: HTMLElement, dimensjon: string): boolean {
    // @ts-ignore
    return element.style[`border${dimensjon}Style`] == 'solid';
}

// typedefinisjoner

export type Celleramme = { venstre: boolean; topp: boolean; høyre: boolean; bunn: boolean; farge: string; tykkelse: number; };

interface TabellcelleRammeKommandoer<ReturnType> {
    /** Setter border på valgte celler.
     * Merk at eventuelle øvrige border/outline attributter satt via 'style' attributtet på {@link TabellCelle} vil bli fjernet */
    setCelleRamme: (ramme: Celleramme) => ReturnType;

    /** Fjerner border/outline stiling fra valgte celler */
    fjernCelleRamme: () => ReturnType;
}

//for at kommandoene skal dukke opp i editor ts typen
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        celleRamme: TabellcelleRammeKommandoer<ReturnType>;
    }
}
